/**
 * Selection Store - Jotai atoms for multi-select card state
 *
 * Manages:
 * - Select mode toggle
 * - Selected card IDs (as Set for O(1) operations)
 * - Range selection tracking
 */

import { atom } from "jotai";

// ============ CORE SELECTION STATE ============

/**
 * Multi-select mode enabled
 * When true, clicking cards toggles selection instead of opening edit
 */
export const selectModeAtom = atom<boolean>(false);

/**
 * Selected booking card IDs (as Set for O(1) lookups)
 * Card ID format: `${employeeId}-${date.toISOString()}`
 */
export const selectedCardIdsAtom = atom<Set<string>>(new Set<string>());

/**
 * Previous selected card ID for shift-click range selection
 */
export const previousSelectedCardAtom = atom<string | null>(null);

/**
 * Current hover target card ID for shift-hover range preview
 * Set when user hovers over a card while holding shift key
 */
export const rangeHoverTargetAtom = atom<string | null>(null);

// ============ DERIVED SELECTION ATOMS ============

/**
 * Derived: Count of selected cards
 */
export const selectionCountAtom = atom((get) => get(selectedCardIdsAtom).size);

/**
 * Derived: Whether any cards are selected
 */
export const hasSelectionAtom = atom(
  (get) => get(selectedCardIdsAtom).size > 0,
);

// ============ SELECTION ACTION ATOMS ============

/**
 * Toggle selection of a single card
 */
export const toggleCardSelectionAtom = atom(
  null,
  (get, set, cardId: string) => {
    const current = get(selectedCardIdsAtom);
    const next = new Set(current);

    if (next.has(cardId)) {
      next.delete(cardId);
    } else {
      next.add(cardId);
    }

    set(selectedCardIdsAtom, next);
    set(previousSelectedCardAtom, cardId);
  },
);

/**
 * Select a range of cards (for shift-click)
 */
export const selectRangeAtom = atom(null, (_get, set, cardIds: string[]) => {
  set(selectedCardIdsAtom, new Set(cardIds));
  if (cardIds.length > 0) {
    set(previousSelectedCardAtom, cardIds[cardIds.length - 1]);
  }
});

/**
 * Add cards to selection (additive)
 */
export const addToSelectionAtom = atom(null, (get, set, cardIds: string[]) => {
  const current = get(selectedCardIdsAtom);
  const next = new Set([...current, ...cardIds]);
  set(selectedCardIdsAtom, next);
  if (cardIds.length > 0) {
    set(previousSelectedCardAtom, cardIds[cardIds.length - 1]);
  }
});

/**
 * Remove cards from selection
 */
export const removeFromSelectionAtom = atom(
  null,
  (get, set, cardIds: string[]) => {
    const current = get(selectedCardIdsAtom);
    const next = new Set(current);
    cardIds.forEach((id) => next.delete(id));
    set(selectedCardIdsAtom, next);
  },
);

/**
 * Clear all selections
 */
export const clearSelectionAtom = atom(null, (_get, set) => {
  set(selectedCardIdsAtom, new Set());
  set(previousSelectedCardAtom, null);
  set(rangeHoverTargetAtom, null);
});

/**
 * Set range hover target (for shift-hover preview)
 */
export const setRangeHoverTargetAtom = atom(
  null,
  (_get, set, cardId: string | null) => {
    set(rangeHoverTargetAtom, cardId);
  },
);

/**
 * Enter select mode
 */
export const enterSelectModeAtom = atom(null, (_get, set) => {
  set(selectModeAtom, true);
});

/**
 * Exit select mode and clear selection
 * Also clears per-employee edit mode to ensure complete reset
 */
export const exitSelectModeAtom = atom(null, (_get, set) => {
  set(selectModeAtom, false);
  set(editingEmployeeIdAtom, null);
  set(selectedCardIdsAtom, new Set());
  set(previousSelectedCardAtom, null);
  set(rangeHoverTargetAtom, null);
});

/**
 * Toggle select mode
 * Also clears per-employee edit mode when exiting to ensure complete reset
 */
export const toggleSelectModeAtom = atom(null, (get, set) => {
  const current = get(selectModeAtom);
  if (current) {
    // Exiting - clear selection and per-employee edit mode
    set(editingEmployeeIdAtom, null);
    set(selectedCardIdsAtom, new Set());
    set(previousSelectedCardAtom, null);
    set(rangeHoverTargetAtom, null);
  }
  set(selectModeAtom, !current);
});

// ============ PER-EMPLOYEE EDIT MODE ============

/**
 * Employee ID currently in per-employee edit mode
 * null when in global edit mode or not editing
 */
export const editingEmployeeIdAtom = atom<number | null>(null);

/**
 * Enter per-employee edit mode for a specific employee
 * Note: Does NOT clear selection - this allows preserving a pre-selected card
 * when entering edit mode (Bug #8993 fix)
 */
export const enterEmployeeEditModeAtom = atom(
  null,
  (_get, set, employeeId: number) => {
    set(selectModeAtom, true);
    set(editingEmployeeIdAtom, employeeId);
    // Don't clear selection - allow pre-selected cards to be preserved
    // Selection is cleared when EXITING edit mode (exitEmployeeEditModeAtom)
    set(previousSelectedCardAtom, null);
  },
);

/**
 * Exit per-employee edit mode (also exits select mode)
 */
export const exitEmployeeEditModeAtom = atom(null, (_get, set) => {
  set(selectModeAtom, false);
  set(editingEmployeeIdAtom, null);
  set(selectedCardIdsAtom, new Set());
  set(previousSelectedCardAtom, null);
  set(rangeHoverTargetAtom, null);
});
